import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import type {
  ConversationParticipant,
  ConversationParticipantCandidate
} from "../types"
import type { MessageApiService } from "../services/api-service"

/**
 * Everyone on a thread, plus adding and removing them.
 *
 * **Adding and removing changes notifications, never access.** Somebody removed keeps whatever
 * ability to open the thread their contextual access on the parent entity gives them — they just stop
 * being told about it. Every string this hook feeds the UI has to keep that distinction; "remove from
 * chat" is honest, "remove access" is not.
 *
 * Loads lazily. A participant list costs a directory lookup on the server, and most people open the
 * panel to read messages rather than to audit who is on it, so nothing is fetched until asked for.
 *
 * **Entity-scoped, strictly.** Every piece of state here belongs to one entity. The list and the
 * candidate picker are surfaced only when the loaded key matches the CURRENT entity — checked in
 * render, so re-pointing the panel to another thread shows an empty list synchronously, with no
 * painted frame of the previous thread's people. Every async write (load, search, and a participant
 * mutation) is guarded against the entity having changed under it. This matters because the panel's
 * participants follow the selection and the component is not remounted on that change: without the
 * guards a click on a stale row would mute/add somebody against the newly selected thread.
 */
export function useConversationParticipants({
  api,
  entityTypeId,
  entityId,
  enabled
}: {
  api: MessageApiService | null
  entityTypeId: string
  entityId: string
  enabled: boolean
}) {
  const [participants, setParticipants] = useState<ConversationParticipant[]>([])
  const [candidates, setCandidates] = useState<ConversationParticipantCandidate[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [pendingProfileIds, setPendingProfileIds] = useState<Set<number>>(new Set())
  const [error, setError] = useState<string | null>(null)

  const entityKey = `${entityTypeId}:${entityId}`
  const params = useMemo(
    () => ({ entityTypeId, entityId }),
    [entityTypeId, entityId]
  )

  // The entity the hook is CURRENTLY pointed at, tracked in render so an async callback that began for
  // one entity can tell the entity has since changed and refuse to write another thread's state.
  const activeKeyRef = useRef(entityKey)
  activeKeyRef.current = entityKey
  const isCurrentEntity = (key: string) => activeKeyRef.current === key

  const load = useCallback(async () => {
    if (!api || !enabled) return

    const key = `${entityTypeId}:${entityId}`
    setIsLoading(true)
    setError(null)

    try {
      const result = await api.fetchParticipants(params)
      if (isCurrentEntity(key)) setParticipants(result)
    } catch (err) {
      if (isCurrentEntity(key)) {
        // A translation KEY, not a message — the hook has no `t`, so the component translates it. Dropping
        // err.message too: it is an internal English service string ("Failed to fetch participants"), not
        // fit for display.
        setError("participants.loadFailed")
      }
    } finally {
      if (isCurrentEntity(key)) setIsLoading(false)
    }
  }, [api, enabled, entityTypeId, entityId, params])

  // Keyed so a re-render with a new `load` identity does not refetch what is already loaded. The
  // effect fired twice per open — once on mount and again as soon as anything upstream re-rendered —
  // which is two identical round trips for one list.
  const loadedKeyRef = useRef<string | null>(null)
  const searchRequestRef = useRef(0)
  useEffect(() => {
    if (!enabled || !api) return

    const key = `${entityTypeId}:${entityId}`
    if (loadedKeyRef.current === key) return

    loadedKeyRef.current = key
    // Invalidate any in-flight search from the previous entity so its late response cannot repopulate
    // this one's candidate list, then drop the previous entity's state before loading. Clear the search
    // spinner here as well: bumping the request id disarms that search's `finally` guard, so without this
    // the new entity could show a search spinner that never resolves.
    searchRequestRef.current++
    setIsSearching(false)
    setParticipants([])
    setCandidates([])
    setPendingProfileIds(new Set())
    setError(null)
    void load()
  }, [enabled, api, entityTypeId, entityId, load])

  // A slower response for an earlier term must not overwrite the current term's candidates — nor may a
  // search that resolves after the entity changed. Guarded by both the monotonic request id (which the
  // entity-change effect also bumps) and the current entity key.
  const search = useCallback(
    async (term: string) => {
      if (!api) return

      const key = activeKeyRef.current
      const requestId = ++searchRequestRef.current
      setIsSearching(true)
      try {
        const result = await api.searchParticipantCandidates(params, term)
        if (requestId === searchRequestRef.current && isCurrentEntity(key)) setCandidates(result)
      } catch {
        // A failed search shows an empty picker rather than an error banner: the list beneath it is
        // still valid, and the person can simply type again.
        if (requestId === searchRequestRef.current && isCurrentEntity(key)) setCandidates([])
      } finally {
        if (requestId === searchRequestRef.current && isCurrentEntity(key)) setIsSearching(false)
      }
    },
    [api, params]
  )

  const setParticipant = useCallback(
    async (profileId: number, subscribed: boolean) => {
      if (!api) return false

      // The entity this mutation is for. If the panel is re-pointed before it resolves, none of its
      // writes may land on the newly selected thread.
      const key = activeKeyRef.current

      setPendingProfileIds(previous => new Set(previous).add(profileId))

      // Optimistic, because the whole interaction is a toggle and a round trip of dead time reads as
      // a click that did not register.
      setParticipants(previous =>
        previous.map(p => (p.profileId === profileId ? { ...p, subscribed } : p))
      )

      try {
        await api.setParticipant({ ...params, targetProfileId: profileId, subscribed })
        if (isCurrentEntity(key)) {
          // Re-read rather than trusting the optimistic row: adding somebody who was never listed has to
          // make them appear, and only the server knows how they resolved.
          await load()
          // Re-check AFTER the await: the entity can change while load() is in flight, and pruning the
          // candidate is a write against whatever thread is now current.
          if (isCurrentEntity(key)) {
            setCandidates(previous => previous.filter(c => c.profileId !== profileId))
          }
        }
        return true
      } catch (err) {
        if (isCurrentEntity(key)) {
          setParticipants(previous =>
            previous.map(p => (p.profileId === profileId ? { ...p, subscribed: !subscribed } : p))
          )
          setError("participants.changeFailed")
        }
        return false
      } finally {
        if (isCurrentEntity(key)) {
          setPendingProfileIds(previous => {
            const next = new Set(previous)
            next.delete(profileId)
            return next
          })
        }
      }
    },
    [api, params, load]
  )

  // Surface people, candidates, the error and the search spinner only when the loaded data belongs to
  // the entity currently asked for. Gating in render (rather than clearing in an effect, which commits
  // after paint) is what stops a painted frame of the previous thread's state — and with the write
  // guards above, stops a click on a stale row from acting on the wrong thread.
  const visibleParticipants = loadedKeyRef.current === entityKey ? participants : []
  const visibleCandidates = loadedKeyRef.current === entityKey ? candidates : []
  const visibleError = loadedKeyRef.current === entityKey ? error : null
  const visibleIsSearching = loadedKeyRef.current === entityKey ? isSearching : false

  /**
   * Everyone on the thread, muted included.
   *
   * Counting only the unmuted made the pill drop when somebody muted themselves, which reads as them
   * having left — and they have not; they are still a participant who simply stopped being notified.
   */
  const participantCount = useMemo(() => visibleParticipants.length, [visibleParticipants])

  const subscribedCount = useMemo(
    () => visibleParticipants.filter(p => p.subscribed).length,
    [visibleParticipants]
  )

  return {
    participants: visibleParticipants,
    subscribedCount,
    participantCount,
    candidates: visibleCandidates,
    isLoading,
    isSearching: visibleIsSearching,
    pendingProfileIds,
    error: visibleError,
    reload: load,
    search,
    setParticipant
  }
}
